import get from 'lodash/get';
import { createSelector } from 'reselect';
import Decimal from 'decimal.js';
import pick from 'lodash/pick';
import { getQueryParams } from 'store/router/routerSelectors/getQueryParams';
import { numberFromString } from 'lib/number';
import { getTotalPayableAtBooking } from 'store/quote/quoteSelectors';
import { DEPOSIT_PAY_PERCENTAGE } from 'config';

export const getFormData = (state) => get(state, 'checkout.formData');
export const getPayments = (state) => get(state, 'checkout.formData.payments');
export const getBookingClientRequestId = (state) => get(state, 'checkout.bookingClientRequestId');
export const getBookingChannel = (state) => get(state, 'checkout.bookingChannel');

export const getPayWith = createSelector(getQueryParams, (queryParams) => queryParams.payWith);

export const getPaymentMode = createSelector(getFormData, (formData) => get(formData, 'paymentMode'));

export const getTravelArranger = createSelector(getFormData, (formData) =>
  pick(formData, ['title', 'firstName', 'lastName', 'emailAddress']),
);

export const getInitialCashAmount = createSelector(getQueryParams, (queryParams) => {
  const amount = numberFromString(queryParams.initialCash);
  return new Decimal(amount || 0);
});

export const getVoucherAmount = createSelector(getPayments, (payments) => payments?.voucher?.amount ?? new Decimal(0));

export const getTravelPassAmount = createSelector(getPayments, (payments) => payments?.travelPass?.amount ?? new Decimal(0));

export const getTravelPassBin = createSelector(getPayments, (payments) => payments?.travelPass?.bin);

export const getTravelPassEncryptedCard = createSelector(getPayments, (payments) => ({
  encryptedCardNumber: payments?.travelPass?.encryptedCardNumber,
  encryptedExpiryMonth: payments?.travelPass?.encryptedExpiryMonth,
  encryptedExpiryYear: payments?.travelPass?.encryptedExpiryYear,
}));

export const getPointsAmount = createSelector(getPayments, (payments) => payments?.points?.amount ?? new Decimal(0));

export const getPointsAmountInCash = createSelector(getPayments, (payments) => payments?.points?.amountInCash ?? new Decimal(0));

export const getPayableNowCashAmount = createSelector(getPayments, (payments) => payments?.cash?.payableNow?.amount ?? new Decimal(0));

export const getPayableLaterCashAmount = createSelector(getPayments, (payments) => payments?.cash?.payableLater?.amount ?? new Decimal(0));

export const getTotalPayableCashAmount = createSelector(
  getPayableNowCashAmount,
  getPayableLaterCashAmount,
  (payableNowCashAmount, payableLaterCashAmount) => payableNowCashAmount.plus(payableLaterCashAmount),
);

export const getRequiresCreditCardPayment = createSelector(getTotalPayableCashAmount, (cashTotal) => cashTotal.greaterThan(0));

export const getTotalPayableAfterVoucher = createSelector(
  getTotalPayableAtBooking,
  getVoucherAmount,
  (totalPayableAtBooking, voucherAmount) => totalPayableAtBooking.minus(voucherAmount),
);

export const getTotalPayableAfterCredits = createSelector(
  getTotalPayableAtBooking,
  getVoucherAmount,
  getTravelPassAmount,
  (totalPayableAtBooking, voucherAmount, travelPassAmount) => totalPayableAtBooking.minus(voucherAmount).minus(travelPassAmount),
);

export const getMinimumDepositAmount = createSelector(getTotalPayableAtBooking, (totalPayableAtBooking) => {
  return totalPayableAtBooking.times(DEPOSIT_PAY_PERCENTAGE).dividedBy(100);
});

export const getMastercardSessionData = createSelector(getPayments, (payments) => get(payments, 'cash.creditCard'));

export const getIsCreditCardValid = createSelector(
  getMastercardSessionData,
  getTotalPayableCashAmount,
  (sessionData, totalPayableCashAmount) =>
    totalPayableCashAmount.isZero() ||
    !!(
      sessionData.sessionId &&
      sessionData.cardNumber &&
      sessionData.expiryMonth &&
      sessionData.expiryYear &&
      sessionData.securityCode &&
      sessionData.nameOnCard
    ),
);
